import { t } from "@lingui/macro";
import { useState } from "react";
import {
  Badge,
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@reactive-resume/ui";
import {
  BellIcon,
  CaretCircleLeftIcon,
  CaretCircleRightIcon,
  ExportIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
  TicketIcon,
  UserCheckIcon,
  PlusIcon,
} from "@phosphor-icons/react";
import { useCoupons } from "@/client/services/coupon/coupons";
import { useFilterCoupon } from "@/client/stores/filter";
import { useDialog } from "@/client/stores/dialog";
import { useExportCoupon } from "@/client/services/coupon/export-coupon";
import qs from "qs";
import { Column, DataTable } from "../_components/datatables";
import { CardContent, CardRoot, TitleCard } from "../_components/card";

export const CouponAdminPage = () => {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<"all" | "valid" | "expired" | "inactive">("all");

  const { filter, replaceFilter, resetFilter } = useFilterCoupon();
  const { coupons, error, loading } = useCoupons(page, filter);
  const { open } = useDialog("filter-coupon");
  const diologCreate = useDialog("create-coupon");
  const { refetch } = useExportCoupon(qs.stringify(filter));

  const handleBackpage = () => setPage(page - 1);
  const handleForwardpage = () => setPage(page + 1);
  const handleSpecficpage = (newpage: number) => setPage(newpage);

  const applyQuickFilters = () => {
    const nextFilter = { ...filter };
    delete nextFilter.code;
    delete nextFilter.status;

    if (search.trim()) {
      nextFilter.code = { contains: search.trim() };
    }
    if (status !== "all") {
      // Votre logique métier utilise status === 0 pour "valide"
      if (status === "valid") nextFilter.status = { equals: 0 };
      else if (status === "inactive") nextFilter.status = { equals: 1 };
      else nextFilter.endDate = { lt: new Date() }; // Expiré = endDate passée
    }

    setPage(1);
    replaceFilter(nextFilter);
  };

  const resetFilters = () => {
    setSearch("");
    setStatus("all");
    setPage(1);
    resetFilter();
  };

  // Colonnes basées sur les données de CouponTable (Code, Montant, Date, Statut)
  const columns: Column<any>[] = [
    {
      key: "code",
      header: "Code",
      render: (item) => (
        <span className="font-mono font-bold text-admin-text-primary">{item.code}</span>
      ),
    },
    {
      key: "amount",
      header: "Montant",
      render: (item) => (
        <span className="text-admin-text-secondary">
          {item.amount} {item.currency || ""}
        </span>
      ),
    },
    {
      key: "expiresAt",
      header: "Date d'expiration",
      render: (item) => (
        // Utilisation de toLocaleString() comme demandé
        <span className="text-admin-text-secondary">
          {new Date(item.expiresAt || item.createdAt).toLocaleString()}
        </span>
      ),
    },
    {
      key: "status",
      header: "Statut",
      render: (item) => (
        // Votre logique métier : status === 0 est valide
        <Badge variant={item.status === 0 ? "success" : item.status === 1 ? "warning" : "error"}>
          {item.status === 0 ? "Valide" : item.status === 1 ? "Inactif" : "Expiré"}
        </Badge>
      ),
    },
  ];

  return (
    <>
      {/* Header de page */}
      <div className="mb-8 flex flex-col items-start justify-between gap-4 lg:flex-row lg:items-center">
        <div>
          <h1 className="text-3xl font-bold text-admin-text-primary">Coupons</h1>
          <p className="mt-1 text-sm text-admin-text-muted">{t`Tableau de bord / Coupons`}</p>
        </div>
        <div className="flex items-center gap-4">
          <div className="relative hidden md:block">
            <MagnifyingGlassIcon
              className="absolute left-3 top-1/2 -translate-y-1/2 text-admin-text-muted"
              size={18}
            />
            <Input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              type="text"
              placeholder={t`Rechercher des coupons…`}
              className="h-10 rounded-full bg-admin-surface pl-10 pr-4 text-admin-text-primary"
            />
          </div>
          <Button
            variant="outline"
            size="icon"
            aria-label="Notifications"
            className="rounded-full bg-admin-surface text-admin-text-secondary hover:text-admin-text-primary"
          >
            <BellIcon size={18} />
          </Button>
          <div className="h-8 w-8 overflow-hidden rounded-full bg-admin-primary">
            <img src="/avatar.jpg" alt={t`Utilisateur`} className="h-full w-full object-cover" />
          </div>
        </div>
      </div>

      {/* Cartes de Statistiques */}
      <div className="mb-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        <CardRoot>
          <CardContent>
            <TitleCard
              text="Coupons affichés"
              number={coupons?.data?.length.toString() ?? "0"}
              classNameText=""
              classNameNumber=""
            />
            <div className="rounded-full bg-admin-surface-elevated p-3 text-admin-text-secondary">
              <TicketIcon size={24} weight="duotone" />
            </div>
          </CardContent>
          <div className="px-5 pb-5">
            <span className="text-xs text-admin-success">▲ 4.2%</span>{" "}
            <span className="text-xs text-admin-text-muted">{t`par rapport au mois dernier`}</span>
          </div>
        </CardRoot>

        <CardRoot>
          <CardContent>
            <TitleCard
              text="Coupons valides"
              number={
                coupons?.data?.filter((coupon) => coupon.status === 0).length.toString() ?? "0"
              }
              classNameText=""
              classNameNumber=""
            />
            <div className="rounded-full bg-admin-success/20 p-3 text-admin-success">
              <UserCheckIcon size={24} weight="duotone" />
            </div>
          </CardContent>
          <div className="px-5 pb-5">
            <span className="text-xs text-admin-success">▲ 3.7%</span>{" "}
            <span className="text-xs text-admin-text-muted">{t`par rapport au mois dernier`}</span>
          </div>
        </CardRoot>

        <CardRoot>
          <CardContent>
            <TitleCard
              text="Page actuelle"
              number={page.toString()}
              classNameText=""
              classNameNumber=""
            />
            <div className="rounded-full bg-admin-accent/20 p-3 text-admin-accent">
              <TicketIcon size={24} weight="duotone" />
            </div>
          </CardContent>
          <div className="px-5 pb-5">
            <span className="text-xs text-admin-accent">● Live</span>{" "}
            <span className="text-xs text-admin-text-muted">{t`mis à jour`}</span>
          </div>
        </CardRoot>

        <CardRoot>
          <CardContent>
            <TitleCard
              text="Pages totales"
              number={coupons?.page?.toString() ?? "0"}
              classNameText=""
              classNameNumber=""
            />
            <div className="bg-admin-warning/20 text-admin-warning rounded-full p-3">
              <TicketIcon size={24} weight="duotone" />
            </div>
          </CardContent>
          <div className="px-5 pb-5">
            <span className="text-admin-warning text-xs">▲ 1.3%</span>{" "}
            <span className="text-xs text-admin-text-muted">{t`par rapport au mois dernier`}</span>
          </div>
        </CardRoot>
      </div>

      {/* Section Filtres et Actions */}
      <div className="mb-8 rounded-xl-admin border border-admin-border bg-admin-surface p-6">
        <div className="flex flex-col flex-wrap justify-between gap-4 lg:flex-row">
          {/* Recherche */}
          <div className="relative min-w-[250px] flex-1">
            <MagnifyingGlassIcon
              className="absolute left-3 top-1/2 -translate-y-1/2 text-admin-text-muted"
              size={18}
            />
            <Input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              type="text"
              placeholder={t`Rechercher par code…`}
              className="h-10 w-full bg-admin-background pl-10 text-admin-text-primary"
            />
          </div>

          {/* Filtres Selects */}
          <div className="flex flex-wrap gap-4">
            <div className="flex flex-col gap-1">
              <span className="text-xs font-medium text-admin-text-muted">Statut</span>
              <Select value={status} onValueChange={(value) => setStatus(value as typeof status)}>
                <SelectTrigger className="h-10 min-w-32 bg-admin-background text-admin-text-secondary">
                  <SelectValue placeholder={t`Tous les statuts`} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t`Tous les statuts`}</SelectItem>
                  <SelectItem value="valid">Valide</SelectItem>
                  <SelectItem value="inactive">Inactif</SelectItem>
                  <SelectItem value="expired">Expiré</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-end gap-3">
              <Button
                variant="outline"
                onClick={applyQuickFilters}
                className="h-10 border-admin-border bg-admin-background text-admin-text-secondary hover:bg-admin-surface-hover hover:text-admin-text-primary"
              >
                <FunnelIcon size={16} className="mr-2" /> Filter
              </Button>
              <Button
                variant="outline"
                onClick={() => open("create", { id: "filter-coupon" })}
                className="h-10 border-admin-border bg-admin-background text-admin-text-secondary hover:bg-admin-surface-hover hover:text-admin-text-primary"
              >
                <FunnelIcon size={16} className="mr-2" /> Advanced filters
              </Button>
              <Button
                variant="ghost"
                onClick={resetFilters}
                className="h-10 text-admin-text-muted hover:text-admin-text-primary"
              >
                Reset
              </Button>
              <Button
                variant="outline"
                onClick={() => refetch()}
                className="h-10 bg-admin-background text-admin-text-secondary hover:text-admin-text-primary"
              >
                <ExportIcon size={16} className="mr-2" /> Export
              </Button>
              <Button
                onClick={() => diologCreate.open("create", { id: "create-coupon" })}
                className="h-10 bg-admin-primary text-admin-primary-foreground hover:bg-admin-primary/90"
              >
                <PlusIcon size={16} className="mr-2" />
                {t`Nouveau coupon`}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Tableau & Pagination */}
      <DataTable
        title=""
        data={coupons?.data ?? []}
        columns={columns}
        pageSize={10}
        className="shadow-sm-admin"
      />

      {/* Pagination (Style UserAdminPage) */}
      <div className="my-3 flex flex-row justify-end space-x-3">
        {page > 1 && (
          <Button variant="outline" onClick={handleBackpage} className="rounded-lg">
            <CaretCircleLeftIcon size={32} weight="regular" />
          </Button>
        )}
        {page - 2 > 0 && (
          <Button
            variant="outline"
            onClick={() => handleSpecficpage(page - 2)}
            className="rounded-lg"
          >
            {page - 2}
          </Button>
        )}
        {page - 1 > 0 && (
          <Button
            variant="outline"
            onClick={() => handleSpecficpage(page - 1)}
            className="rounded-lg"
          >
            {page - 1}
          </Button>
        )}

        <Button variant="secondary" className="bg-admin-primary text-admin-primary-foreground">
          {page}
        </Button>
        {coupons && page + 1 <= coupons.page && (
          <Button
            variant="outline"
            onClick={() => handleSpecficpage(page + 1)}
            className="rounded-lg"
          >
            {page + 1}
          </Button>
        )}
        {coupons && page + 2 <= coupons.page && (
          <Button
            variant="outline"
            onClick={() => handleSpecficpage(page + 2)}
            className="rounded-lg"
          >
            {page + 2}
          </Button>
        )}
        {coupons && page < coupons.page && (
          <Button variant="outline" onClick={handleForwardpage} className="rounded-lg">
            <CaretCircleRightIcon size={32} weight="regular" />
          </Button>
        )}
      </div>
    </>
  );
};
